import RenderIfVisible from '@/Utility/RenderIfVisible';
import { getLocalStorageItem, useLocalState } from '@/Utility/hooks';
import { Box, type BoxProps, Grid, type GridProps } from '@mui/material';
import { type ComponentProps, useEffect, useMemo } from 'react';
import type { PostsLoaderTypeKey, PostsLoaderTypes } from '../posts/loaders';
import { useMultiViewPickerStore } from './MultiViewPickerStore';
import ActionBar from './action-bar/ActionBar';
import { GridCard, type GridCardProps, ListView, type ListViewProps, SelectView } from './views';

import './MultiViewPicker.less';

type Props<T extends PostsLoaderTypeKey> = {
  type: T;
  data: Array<PostsLoaderTypes[T]['paginationNodeType']>;
  gridProps: (data: PostsLoaderTypes[T]['paginationNodeType']) => GridCardProps;
  gridSelectView?: boolean;
  // Only headerProps + listProps
  tableProps?: Omit<ListViewProps<T>, 'data' | 'loadMore' | 'type'>;
  loadMore?: () => void;
  isFallback?: boolean;
  slotProps?: {
    root?: BoxProps;
    actionBar?: ComponentProps<typeof ActionBar>['slotProps'];
    gridRoot?: GridProps;
  };
};

type ViewType = 'grid' | 'list';

// This hook is used to handle the shift key press event -> update the store
const useShiftSelect = () => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        useMultiViewPickerStore.getState().setShiftKeyPressed(true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        useMultiViewPickerStore.getState().setShiftKeyPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);
};

const MultiViewPicker = <T extends PostsLoaderTypeKey>(props: Props<T> & { view: ViewType }) => {
  useShiftSelect();
  // when we grab the view from localstorage, it might be null -> due react re-renders
  if (!props?.view) {
    return <></>;
  }
  if (props.data?.length === 0) return <></>;

  if (props?.gridSelectView) {
    return <SelectView gridProps={props.gridProps} data={props.data} loadMore={props?.loadMore} />;
  }

  if (props?.view === 'grid') {
    return (
      <Grid
        container
        className="GridContainer"
        justifyContent={'flex-start'}
        spacing={4}
        {...props?.slotProps?.gridRoot}
      >
        {props.data.map((item, idx) => (
          <RenderIfVisible
            key={`zngly_grid_list_grid_${idx.toString()}`}
            defaultHeight={350}
            fallback={
              <GridCard
                key={`zngly_grid_list_card_fallback_${idx.toString()}`}
                {...props.gridProps(item)}
                node={item}
                isFallback={true}
                showCheckbox={true}
                isLast={false}
              />
            }
            onChange={(isVisible) => {
              if (!isVisible || idx !== props.data.length - 1) return;
              props?.loadMore?.();
            }}
            rootElement={<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }} />}
          >
            <GridCard
              key={`zngly_grid_list_card_${idx.toString()}_${item?.id}`}
              {...props.gridProps(item)}
              node={item}
              isFallback={props?.isFallback}
              showCheckbox={true}
              isLast={idx === props.data.length - 1}
            />
          </RenderIfVisible>
        ))}
      </Grid>
    );
  }

  if (!!props?.tableProps) {
    return (
      <ListView
        type={props.type}
        headerProps={props.tableProps?.headerProps}
        // @ts-ignore
        listProps={props.tableProps?.listProps}
        data={props.data}
        loadMore={props?.loadMore}
        isFallback={props?.isFallback}
        showCheckbox={true}
      />
    );
  }

  return <></>;
};

const MultiViewPickerWrapper = <T extends PostsLoaderTypeKey>(props: Props<T>) => {
  // calculate the inital view value from localstorage -> if its null then set it to grid
  const initViewValue = useMemo<ViewType>(() => {
    const value = getLocalStorageItem(`mvp${window.location.pathname}`) as ViewType;
    if (!['grid', 'list'].includes(value)) {
      return 'grid';
    }
    return value;
  }, []);

  const [view, setView] = useLocalState<ViewType>(`mvp${window.location.pathname}`, initViewValue);

  useEffect(() => {
    const updatedNodes = new Map(
      props.data
        .filter((item) => item?.id)
        .map((item) => {
          return [item.id, { checked: false, node: item }];
        })
    );

    useMultiViewPickerStore.getState().updateNodes(updatedNodes);
  }, [props?.data]);

  useEffect(() => {
    if (!['grid', 'list'].includes(initViewValue)) {
      setView('grid');
      return;
    }
  }, [initViewValue]);

  useEffect(() => {
    return () => {
      useMultiViewPickerStore.getState().reset();
    };
  }, []);

  if (!initViewValue) {
    return <></>;
  }
  if (props.data?.length === 0) return <></>;
  return (
    <Box className="multiViewPicker-container" {...props?.slotProps?.root}>
      <ActionBar
        hasTableProps={!!props?.tableProps}
        hasGridSelectView={!!props?.gridSelectView}
        view={view}
        setView={setView}
        slotProps={props?.slotProps?.actionBar}
      />
      <MultiViewPicker {...props} view={view} />
    </Box>
  );
};

export default MultiViewPickerWrapper;
